/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { FlightFollowingField } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CompanyProps {
  id: string;
  name: string;
  abbreviation: string;
  companyCode: string;
  includeCompetitors: boolean;
  hasPrivateUnits: boolean;
  isOemCompany: boolean;
  imageUri: string | null;
  gLCode: string | null;
  gpsFeedResetsPositionReportTime: boolean;
  callNumberPrefix: string | null;
  timezoneCd: string | null;
  flightFollowingFields: Set<FlightFollowingField> | null;
  requireSkipReasonsForNonCompanyTransportOffers: boolean | null;
  crewPatientInfoPageNotificationTemplateId: string | null;
  unitRemainsInServiceWithNoVehicle: boolean;
  enableNotificationGroupTypes: boolean;
  transportCheckTimerDurationInMinutes: number | null;
  restrictRiskAnalysisAcknowledgementToOCCRole: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CompanyRecord extends Record<CompanyProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  companyCode: null as any,
  includeCompetitors: null as any,
  hasPrivateUnits: null as any,
  isOemCompany: null as any,
  imageUri: null as any,
  gLCode: null as any,
  gpsFeedResetsPositionReportTime: null as any,
  callNumberPrefix: null as any,
  timezoneCd: null as any,
  flightFollowingFields: null as any,
  requireSkipReasonsForNonCompanyTransportOffers: null as any,
  crewPatientInfoPageNotificationTemplateId: null as any,
  unitRemainsInServiceWithNoVehicle: null as any,
  enableNotificationGroupTypes: null as any,
  transportCheckTimerDurationInMinutes: null as any,
  restrictRiskAnalysisAcknowledgementToOCCRole: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    companyCode: string;
    includeCompetitors: boolean;
    hasPrivateUnits: boolean;
    isOemCompany: boolean;
    imageUri: string | null;
    gLCode: string | null;
    gpsFeedResetsPositionReportTime: boolean;
    callNumberPrefix: string | null;
    timezoneCd: string | null;
    flightFollowingFields: any;
    requireSkipReasonsForNonCompanyTransportOffers: boolean | null;
    crewPatientInfoPageNotificationTemplateId: string | null;
    unitRemainsInServiceWithNoVehicle: boolean;
    enableNotificationGroupTypes: boolean;
    transportCheckTimerDurationInMinutes: number | null;
    restrictRiskAnalysisAcknowledgementToOCCRole: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CompanyRecord {
    if (json == null) {
      throw new Error('CompanyRecord.fromJS: parameter cannot be null');
    }
    return new CompanyRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CompanyJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'CompanyJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'CompanyJson.abbreviation'),
      companyCode: getFromOrThrow(json, (j) => j.companyCode, 'CompanyJson.companyCode'),
      includeCompetitors: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'includeCompetitors', false),
        'CompanyJson.includeCompetitors',
      ),
      hasPrivateUnits: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'hasPrivateUnits', false),
        'CompanyJson.hasPrivateUnits',
      ),
      isOemCompany: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isOemCompany', false),
        'CompanyJson.isOemCompany',
      ),
      imageUri: json.imageUri,
      gLCode: json.gLCode,
      gpsFeedResetsPositionReportTime: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'gpsFeedResetsPositionReportTime', false),
        'CompanyJson.gpsFeedResetsPositionReportTime',
      ),
      callNumberPrefix: json.callNumberPrefix,
      timezoneCd: json.timezoneCd,
      flightFollowingFields:
        json.flightFollowingFields != null
          ? Set<FlightFollowingField>(json.flightFollowingFields)
          : null,
      requireSkipReasonsForNonCompanyTransportOffers:
        json.requireSkipReasonsForNonCompanyTransportOffers,
      crewPatientInfoPageNotificationTemplateId: json.crewPatientInfoPageNotificationTemplateId,
      unitRemainsInServiceWithNoVehicle: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitRemainsInServiceWithNoVehicle', false),
        'CompanyJson.unitRemainsInServiceWithNoVehicle',
      ),
      enableNotificationGroupTypes: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'enableNotificationGroupTypes', false),
        'CompanyJson.enableNotificationGroupTypes',
      ),
      transportCheckTimerDurationInMinutes: json.transportCheckTimerDurationInMinutes,
      restrictRiskAnalysisAcknowledgementToOCCRole: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'restrictRiskAnalysisAcknowledgementToOCCRole', false),
        'CompanyJson.restrictRiskAnalysisAcknowledgementToOCCRole',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    companyCode: string;
    includeCompetitors: boolean;
    hasPrivateUnits: boolean;
    isOemCompany: boolean;
    imageUri: string | null;
    gLCode: string | null;
    gpsFeedResetsPositionReportTime: boolean;
    callNumberPrefix: string | null;
    timezoneCd: string | null;
    flightFollowingFields: any;
    requireSkipReasonsForNonCompanyTransportOffers: boolean | null;
    crewPatientInfoPageNotificationTemplateId: string | null;
    unitRemainsInServiceWithNoVehicle: boolean;
    enableNotificationGroupTypes: boolean;
    transportCheckTimerDurationInMinutes: number | null;
    restrictRiskAnalysisAcknowledgementToOCCRole: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CompanyRecord, Error[]] {
    if (json == null) {
      throw new Error('CompanyRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CompanyRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CompanyJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'CompanyJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'CompanyJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyCode: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.companyCode, 'CompanyJson.companyCode'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeCompetitors: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'includeCompetitors', false),
            'CompanyJson.includeCompetitors',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hasPrivateUnits: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'hasPrivateUnits', false),
            'CompanyJson.hasPrivateUnits',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOemCompany: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isOemCompany', false),
            'CompanyJson.isOemCompany',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      imageUri: tryOrDefault(
        () => json.imageUri,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gLCode: tryOrDefault(
        () => json.gLCode,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gpsFeedResetsPositionReportTime: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'gpsFeedResetsPositionReportTime', false),
            'CompanyJson.gpsFeedResetsPositionReportTime',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      callNumberPrefix: tryOrDefault(
        () => json.callNumberPrefix,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      flightFollowingFields: tryOrDefault(
        () =>
          json.flightFollowingFields != null
            ? Set<FlightFollowingField>(json.flightFollowingFields)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requireSkipReasonsForNonCompanyTransportOffers: tryOrDefault(
        () => json.requireSkipReasonsForNonCompanyTransportOffers,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewPatientInfoPageNotificationTemplateId: tryOrDefault(
        () => json.crewPatientInfoPageNotificationTemplateId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitRemainsInServiceWithNoVehicle: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitRemainsInServiceWithNoVehicle', false),
            'CompanyJson.unitRemainsInServiceWithNoVehicle',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      enableNotificationGroupTypes: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'enableNotificationGroupTypes', false),
            'CompanyJson.enableNotificationGroupTypes',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportCheckTimerDurationInMinutes: tryOrDefault(
        () => json.transportCheckTimerDurationInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      restrictRiskAnalysisAcknowledgementToOCCRole: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'restrictRiskAnalysisAcknowledgementToOCCRole', false),
            'CompanyJson.restrictRiskAnalysisAcknowledgementToOCCRole',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CompanyProps) {
    super(props);
    if (props == null) {
      throw new Error('CompanyRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CompanyViewModelBase implements CompanyProps {
  protected readonly _props: CompanyProps;
  private _json?: CompanyProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get companyCode(): string {
    return this._props.companyCode;
  }
  get includeCompetitors(): boolean {
    return this._props.includeCompetitors;
  }
  get hasPrivateUnits(): boolean {
    return this._props.hasPrivateUnits;
  }
  get isOemCompany(): boolean {
    return this._props.isOemCompany;
  }
  get imageUri(): string | null {
    return this._props.imageUri;
  }
  get gLCode(): string | null {
    return this._props.gLCode;
  }
  get gpsFeedResetsPositionReportTime(): boolean {
    return this._props.gpsFeedResetsPositionReportTime;
  }
  get callNumberPrefix(): string | null {
    return this._props.callNumberPrefix;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get flightFollowingFields(): Set<FlightFollowingField> | null {
    return this._props.flightFollowingFields;
  }
  get requireSkipReasonsForNonCompanyTransportOffers(): boolean | null {
    return this._props.requireSkipReasonsForNonCompanyTransportOffers;
  }
  get crewPatientInfoPageNotificationTemplateId(): string | null {
    return this._props.crewPatientInfoPageNotificationTemplateId;
  }
  get unitRemainsInServiceWithNoVehicle(): boolean {
    return this._props.unitRemainsInServiceWithNoVehicle;
  }
  get enableNotificationGroupTypes(): boolean {
    return this._props.enableNotificationGroupTypes;
  }
  get transportCheckTimerDurationInMinutes(): number | null {
    return this._props.transportCheckTimerDurationInMinutes;
  }
  get restrictRiskAnalysisAcknowledgementToOCCRole(): boolean {
    return this._props.restrictRiskAnalysisAcknowledgementToOCCRole;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CompanyProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        companyCode: this._props.companyCode,
        includeCompetitors: this._props.includeCompetitors,
        hasPrivateUnits: this._props.hasPrivateUnits,
        isOemCompany: this._props.isOemCompany,
        imageUri: this._props.imageUri,
        gLCode: this._props.gLCode,
        gpsFeedResetsPositionReportTime: this._props.gpsFeedResetsPositionReportTime,
        callNumberPrefix: this._props.callNumberPrefix,
        timezoneCd: this._props.timezoneCd,
        flightFollowingFields: this._props.flightFollowingFields,
        requireSkipReasonsForNonCompanyTransportOffers:
          this._props.requireSkipReasonsForNonCompanyTransportOffers,
        crewPatientInfoPageNotificationTemplateId:
          this._props.crewPatientInfoPageNotificationTemplateId,
        unitRemainsInServiceWithNoVehicle: this._props.unitRemainsInServiceWithNoVehicle,
        enableNotificationGroupTypes: this._props.enableNotificationGroupTypes,
        transportCheckTimerDurationInMinutes: this._props.transportCheckTimerDurationInMinutes,
        restrictRiskAnalysisAcknowledgementToOCCRole:
          this._props.restrictRiskAnalysisAcknowledgementToOCCRole,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCompanyEntities = (state: AppState) => state.entityStores.company.entities;

export const selectCompanyById = (id: string) => (state: AppState) =>
  state.entityStores.company.entities.get(id);

export const selectCompanyMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.company.meta.get(id);

export const selectCompanyAndMetadataById = (id: string) =>
  createSelector(selectCompanyById(id), selectCompanyMetadataById(id), (company, meta) => ({
    company,
    meta,
  }));

export function selectCompanyEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCompanyEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CompanyRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCompanyEntitiesByName(name: string) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectCompanyEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectCompanyEntitiesByCompanyCode(companyCode: string) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.companyCode === companyCode).valueSeq(),
  );
}

export function selectCompanyEntitiesByIncludeCompetitors(includeCompetitors: boolean) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.includeCompetitors === includeCompetitors).valueSeq(),
  );
}

export function selectCompanyEntitiesByHasPrivateUnits(hasPrivateUnits: boolean) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.hasPrivateUnits === hasPrivateUnits).valueSeq(),
  );
}

export function selectCompanyEntitiesByIsOemCompany(isOemCompany: boolean) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.isOemCompany === isOemCompany).valueSeq(),
  );
}

export function selectCompanyEntitiesByImageUri(imageUri: string) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.imageUri === imageUri).valueSeq(),
  );
}

export function selectCompanyEntitiesByGLCode(gLCode: string) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.gLCode === gLCode).valueSeq(),
  );
}

export function selectCompanyEntitiesByGpsFeedResetsPositionReportTime(
  gpsFeedResetsPositionReportTime: boolean,
) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities
      .filter((e) => e.gpsFeedResetsPositionReportTime === gpsFeedResetsPositionReportTime)
      .valueSeq(),
  );
}

export function selectCompanyEntitiesByCallNumberPrefix(callNumberPrefix: string) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.callNumberPrefix === callNumberPrefix).valueSeq(),
  );
}

export function selectCompanyEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectCompanyEntitiesByRequireSkipReasonsForNonCompanyTransportOffers(
  requireSkipReasonsForNonCompanyTransportOffers: boolean,
) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.requireSkipReasonsForNonCompanyTransportOffers ===
          requireSkipReasonsForNonCompanyTransportOffers,
      )
      .valueSeq(),
  );
}

export function selectCompanyEntitiesByCrewPatientInfoPageNotificationTemplateId(
  crewPatientInfoPageNotificationTemplateId: string,
) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.crewPatientInfoPageNotificationTemplateId === crewPatientInfoPageNotificationTemplateId,
      )
      .valueSeq(),
  );
}

export function selectCompanyEntitiesByUnitRemainsInServiceWithNoVehicle(
  unitRemainsInServiceWithNoVehicle: boolean,
) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities
      .filter((e) => e.unitRemainsInServiceWithNoVehicle === unitRemainsInServiceWithNoVehicle)
      .valueSeq(),
  );
}

export function selectCompanyEntitiesByEnableNotificationGroupTypes(
  enableNotificationGroupTypes: boolean,
) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities
      .filter((e) => e.enableNotificationGroupTypes === enableNotificationGroupTypes)
      .valueSeq(),
  );
}

export function selectCompanyEntitiesByTransportCheckTimerDurationInMinutes(
  transportCheckTimerDurationInMinutes: number,
) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities
      .filter(
        (e) => e.transportCheckTimerDurationInMinutes === transportCheckTimerDurationInMinutes,
      )
      .valueSeq(),
  );
}

export function selectCompanyEntitiesByRestrictRiskAnalysisAcknowledgementToOCCRole(
  restrictRiskAnalysisAcknowledgementToOCCRole: boolean,
) {
  return createSelector(selectAllCompanyEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.restrictRiskAnalysisAcknowledgementToOCCRole ===
          restrictRiskAnalysisAcknowledgementToOCCRole,
      )
      .valueSeq(),
  );
}
//#endregion Selectors
